import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button, Drawer, Menu, Checkbox, Tag, Badge, Empty, Spin, Image, Divider } from 'antd';
import useZuStore from 'store/zustand/zustand-store';
import { ze } from 'utils/zhEn';
import { CloseOutlined } from '@ant-design/icons';
import scss from './AiDrawer.scss';
import AiIssueAnalysisDrawerBody from 'components/top/AiDrawer/AiIssueAnalysisDrawerBody';
import AiStackAnalysisDrawerBody from 'components/top/AiDrawer/AiStackAnalysisDrawerBody';
import AiAssemblyAnalysisDrawerBody from 'components/top/AiDrawer/AiAssemblyAnalysisDrawerBody';

const AiDrawerAutoScrollBody = ({
  children,
  forceScrollToBottomTrigger,
}) => {
  const [userScrolled, setUserScrolled] = useState(false);
  const contentRef = useRef(null);
  const observerRef = useRef(null);
  const resizeObserverRef = useRef(null);

  // 处理滚动事件
  const handleScroll = useCallback((e) => {
    if (e.target !== contentRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (Math.abs(scrollHeight - scrollTop - clientHeight) > 10) {
      setUserScrolled(true);
    } else {
      setUserScrolled(false);
    }
  }, []);

  function forceScrollToBottom() {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }

  // 滚动到底部
  const scrollToBottom = useCallback(() => {
    if (!contentRef.current || userScrolled) return;

    const scroll = forceScrollToBottom;
    // 立即执行一次
    scroll();
    // 延迟执行一次，确保内容完全渲染
    setTimeout(scroll, 0);
    // 再次延迟执行，处理可能的异步内容
    setTimeout(scroll, 100);
  }, [userScrolled]);

  useEffect(() => {
    if (!contentRef.current) return;

    // 创建 ResizeObserver 监听内容高度变化
    resizeObserverRef.current = new ResizeObserver(() => {
      scrollToBottom();
    });

    // 创建 MutationObserver 监听 DOM 变化
    observerRef.current = new MutationObserver(() => {
      scrollToBottom();
    });

    // 开始观察
    resizeObserverRef.current.observe(contentRef.current);
    observerRef.current.observe(contentRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // 初始滚动
    scrollToBottom();

    // 清理函数
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [scrollToBottom]);

  useEffect(() => {
    if (forceScrollToBottomTrigger) {
      forceScrollToBottom();
    }
  },[forceScrollToBottomTrigger]);

  return <div
    className={scss.contentBody}
    ref={contentRef}
    onScroll={handleScroll}
  >
    { children }
  </div>;
};

export default AiDrawerAutoScrollBody;
